import axios from '@/plugins/axios';
import qs from 'qs';
import utils from '@/stores/utils';
import $utils from '@/utils';
import { CSV_EXPORT_LIMIT } from '@/constants/csv-export';

const { loadFiltersFromStorage, saveFiltersToStorage } = utils;

const DEFAULT_FILTERS = { offset: 0, limit: 1000 };

const createState = (options = {}) => ({
    permissions: [],
    permissions_filters: DEFAULT_FILTERS,
    permissions_count: 0,
    permissions_loaded: false,
    permissions_loading: false,
    permissions_loading_errors: null,

    permission_deleting: false,
    permission_deleting_errors: null,

    profile: null,
    cancelTokenSource: null,
    _storageOptions: options || {},
});

const getters = {
    apps(state) {
        const uniqueApps = new Set();
        state.permissions.forEach(item => {
            uniqueApps.add(item.app);
        });
        return [...uniqueApps];
    },
    getModelsByApp: (state) => (app) => {
        const models = new Set();
        state.permissions.forEach(item => {
            if (item.app === app) {
                models.add(item.model);
            }
        });
        return Array.from(models);
    },
    getItemsByAppOrdered: (state) => (app) => {
        return state.permissions
            .filter(item => item.app === app)
            .sort((a, b) => {
                // Trie par model, puis par name
                if (a.model === b.model) {
                    return a.name.localeCompare(b.name);
                }
                return a.model.localeCompare(b.model);
            });
    },
}


const mutations = {
    updatePermissions(state, permissions) {
        state.permissions = permissions;
        state.permissions_loaded = true;
    },
    updatePermissionsCount(state, count) {
        state.permissions_count = count;
    },
    updatePermissionsFilters(state, filters) {
        state.permissions_filters = filters;
        const { storageKey, fieldsToSave } = state._storageOptions;
        if (storageKey) {
            saveFiltersToStorage(storageKey, state.permissions_filters, fieldsToSave);
        }
    },
    updatePermissionsLoading(state, loading) {
        state.permissions_loading = loading;
    },
    updatePermissionsLoadingErrors(state, errors) {
        state.permissions_loading_errors = errors;
    },

    updatePermissionDeleting(state, deleting) {
        state.permission_deleting = deleting;
    },
    updatePermissionDeletingErrors(state, errors) {
        state.permission_deleting_errors = errors;
    },
    updatePermissionsLoaded(state, loaded) {
        state.permissions_loaded = loaded;
    },
    updateProfile(state, profile) {
        state.profile = profile;
        state.permissions_filters.profile = profile;
    },
    updateCancelTokenSource(state, source) {
        state.cancelTokenSource = source;
    },
    updateStorageOptions(state, storage_options) {
        state._storageOptions = storage_options;
    },
};

const actions = {

    exportPermissionsAsCSV({ commit, dispatch, state }, params) {
        let filters = {};
        if (params.filtered) {
            filters = Object.assign({}, state.permissions_filters);
            filters.offset = null;
        }
        filters.format = 'csv';
        filters.limit = CSV_EXPORT_LIMIT;
        
        $utils.downloadFile({
            url: "/api/permissions/",
            params: filters,
        }, $utils.getFilename('permissions'));
    },

    async deletePermission({ commit, dispatch, state }, params) {
        const url = `/api/permissions/${params.instance.id}/`;

        commit('updatePermissionDeleting', true);
        commit('updatePermissionDeletingErrors', null);

        try {
            const response = await axios.delete(url);
            commit('updatePermissionDeleting', false);
            return response;
        } catch (xhr_error) {
            const error = utils.handleError(xhr_error);
            commit('updatePermissionDeleting', false);
            commit('updatePermissionDeletingErrors', error.details);
            throw error;
        }
    },

    async fetchPermissions({ commit, dispatch, state }, params) {
        // Cancel any pending request
        if (state.cancelTokenSource) {
            state.cancelTokenSource.cancel("New fetch started");
        }

        // Create new cancel token source
        const source = axios.CancelToken.source();
        commit('updateCancelTokenSource', source);

        commit('updatePermissionsLoading', true);
        commit('updatePermissionsLoadingErrors', null);

        try {
            const response = await axios.get('/api/permissions/', {
                params: state.permissions_filters,
                cancelToken: source.token,
            });
            commit('updatePermissionsLoading', false);
            commit('updatePermissions', response.data.results);
            commit('updatePermissionsCount', response.data.count);
            return response;
        } catch (xhr_error) {
            // Ignore cancelled requests
            if (axios.isCancel(xhr_error)) {
                return;
            }
            const error = utils.handleError(xhr_error);
            commit('updatePermissionsLoadingErrors', error.details);
            commit('updatePermissionsLoading', false);
            throw error;
        }
    },

    async init({ commit, dispatch, state }, params) {
        commit('updateProfile', params?.profile);
        commit('updatePermissionsLoaded', false);
        commit('updatePermissions', []);
        commit('updatePermissionsCount', 0);
        
        // Update storage options if provided
        if (params?.storageOptions !== undefined) {
            await commit('updateStorageOptions', params.storageOptions);
        }
        
        // Load filters from localStorage if storage is configured
        await commit('updatePermissionsFilters', { 
            ...DEFAULT_FILTERS, 
            ...(state._storageOptions?.storageKey ? loadFiltersFromStorage(state._storageOptions?.storageKey, {}) : {}),
            ...(params.filters || {})
        });
    }
};

export default (options = {}) => ({
    namespaced: true,
    state: createState(options),
    mutations,
    getters,
    actions
});